<template>
  <div class="table-box bg-grey">
    <div class="table-box__outer" v-if="!!events.length">
      <div class="table-box__inner">
        <div class="scroll scroll-right scroll-hidden">
          <span class="s s-slider-right"></span>
        </div>
        <table class="table">
          <thead>
            <tr>
              <th>
                <b v-text="translate('lblTableHeaderEventDate')"></b>
              </th>
              <th class="event-info">
                <b v-text="translate('lblTableHeaderEventInfo')"></b>
              </th>
              <th v-if="isAnyEventLocatable">
                <b v-text="translate('lblTableHeaderEventLocation')"></b>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="event in events" :key="event.id">
              <td class="dates">
                <span>{{ formatDate(event.startDate, formatStartDate) }}</span>
                <span v-if="event.endDate"> - {{ formatDate(event.endDate, formatEndDate) }}</span>
              </td>
              <td>
                <b><a :href="getLinkToEventDetailView(event.id)" target="_self" v-text="event.name"></a></b>
                <div v-if="event.additionalText" v-html="event.additionalText"></div>
                <EventCrops :event-crops="event.crops"></EventCrops>
                <div><small>{{ translateRegistrationStatus(event) }}</small></div>
                <div v-if="isLinkToParticipantsViewValid || isEventUnsubscribable(event)" class="event-actions">
                  <span v-if="isEventUnsubscribable(event)" class="event-action">
                    <button
                      type="button" class="button--link"
                      v-text="translate('lblUnsubscribe')"
                      @click="confirm(event)"
                    ></button>
                  </span>
                  <span v-if="isLinkToParticipantsViewValid" class="event-action">
                    <a
                      :href="getLinkToEventParticipantsView(event.id)"
                      v-text="translate('lblParticipantsViewLinkText')"
                    ></a>
                  </span>
                </div>
              </td>
              <td class="location" v-if="isAnyEventLocatable">
                <EventLocation :city="event.city" :postal-code="event.postalCode"></EventLocation>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <notification-modal
      v-if="unsubscribeModal"
      :type="successfullyUnsubscribed ? 'success' : 'error'"
      :title="successfullyUnsubscribed ? translate('lblSuccessfullyUnsubscribed') : translate('lblError')"
      :subtitle="successfullyUnsubscribed ? translate('lblSuccessfullyUnsubscribedFrom') : translate('lblErrorUnsubscribe')"
      :prevent-close="true"
      @okay="unsubscribeModal = false"
    >
      <a
        v-if="successfullyUnsubscribed"
        :href="getLinkToEventDetailView(selectedUnsubscribeEvent.id)"
        class="link-more"
      >{{selectedUnsubscribeEvent.name}}</a>
    </notification-modal>
    <notification-modal
      v-if="confirmModal"
      type="warning"
      :title="translate('lblUnsubscribeFrom') + selectedUnsubscribeEvent.name"
      :subtitle="translate('lblUnsubscribeConfirm')"
      :prevent-close="true"
      :lbl-btn-cancel="translate('lblUnsubscribeAbort')"
      :lbl-btn-okay="translate('lblUnsubscribe')"
      @close="closeConfirmModal"
      @okay="unsubscribeFromEvent"
    >
    </notification-modal>
  </div>
</template>

<script>
import EventMixin from '@/events/mixins/EventMixin';
import EventCrops from '@/events/components/event-crops.vue';
import EventLocation from '@/events/components/event-location.vue';
import NotificationModal from '@/shared/components/NotificationModal.vue';
import EventPermissionsMixin from '@/events/mixins/EventPermissionsMixin';

export default {
  name: 'EventTable',
  components: {
    EventCrops,
    EventLocation,
    NotificationModal,
  },
  props: {
    events: {
      type: Array,
      required: true,
    },
    formatStartDate: {
      type: String,
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
    formatEndDate: {
      type: String,
      required: true,
    },
  },
  mixins: [EventMixin, EventPermissionsMixin],
  inject: ['detailView', 'participantsView'],
  computed: {
    isAnyEventLocatable() {
      return this.events.some((event) => event.postalCode && event.city);
    },
    isLinkToParticipantsViewValid() {
      return !!this.participantsView;
    },
  },
  data() {
    return {
      selectedUnsubscribeEvent: null,
      confirmModal: false,
      unsubscribeModal: false,
      successfullyUnsubscribed: false,
    };
  },
  emits: ['unsubscribeEvent'],
  methods: {
    translateRegistrationStatus(event) {
      const statusTranslationsDict = {
        'Registration active': 'lblRegistrationStatusActive',
        Waiting: 'lblRegistrationStatusWaiting',
        'Registration cancelled': 'lblRegistrationStatusCanceled',
      };
      return this.translate(statusTranslationsDict[event.currentUserRegistrationStatus]);
    },
    closeConfirmModal() {
      this.confirmModal = false;
    },
    confirm(event) {
      this.selectedUnsubscribeEvent = event;
      this.confirmModal = true;
    },
    async unsubscribeFromEvent() {
      this.confirmModal = false;
      this.$root.$emit('showLoadingState', true);
      await Promise.all([
        this.setPermissionsForShowUserDataInParticipantsList(this.selectedUnsubscribeEvent.id, false),
        this.sendUnsubscribeFromEvent(),
      ])
        .then(async () => {
          this.unsubscribeModal = true;
          this.successfullyUnsubscribed = true;
          this.$emit('unsubscribeEvent');
        })
        .catch((err) => {
          this.unsubscribeModal = true;
          this.successfullyUnsubscribed = false;
          console.error(err);
        })
        .finally(() => {
          this.$root.$emit('showLoadingState', false);
        });
    },
    async sendUnsubscribeFromEvent() {
      const id = this.selectedUnsubscribeEvent?.CampaignMembers?.find((member) => member.myUser_ID__c === this.userId)?.Id ?? '';
      const payload = {
        Cancelled__c: {
          value: true,
        },
        CampaignMemberId: {
          value: id,
        },
      };
      return this.$http({
        method: 'put',
        url: `${this.apiUrl}/campaign/events/event`,
        headers: {
          Authorization: `${this.getCookieValue('Authorization')}`,
          'Content-Type': 'application/json',
        },
        data: JSON.stringify(payload),
      });
    },
  },
};
</script>

<style scoped lang="scss">
.dates, .location {
  @media (min-width: 768px) {
    white-space: nowrap;
  }
}
.event-info {
  width: 100%;
}

.event-actions {
  text-align: left;
  font-size: 12px;

  .event-action + .event-action:before {
    content: ' | ';
    color: #333333;
  }
}
</style>
